/* eslint-disable */
// HeaderBell — the header's two alert affordances, extracted out of AppHeader.js.
//
// WHY THIS FILE EXISTS
//   AppHeader.js is on the GOV1 LOC grandfather list (Conv #163): it may shrink,
//   never grow. P2a needed a second affordance in the right cluster, which is
//   additive by nature. Moving the existing deadline button here pays for the
//   new one and leaves AppHeader smaller than it started — the same trade that
//   produced GlobalSearchMount.js.
//
// WHY TWO BUTTONS AND NOT ONE
//   The bell was already taken. It is labelled "Notifications" but it opens the
//   DEADLINE panel and its badge counts overdue developments
//   (useDeadlineNotifications). Folding the notification count into it would
//   produce one number meaning two unrelated things, behind one click target
//   that reaches only one of them — the badge-starts-lying failure the single
//   -array rule in useNotifications exists to prevent. So: bell keeps deadlines,
//   megaphone takes notifications, each with its own count and its own label.
//
//   The deadline button below is a VERBATIM move — same classes, same handler,
//   same markup — except its aria-label, which said "Notifications" and was
//   simply wrong. It is now "Deadline alerts". Its title text was already
//   correct and is untouched.
//
//   DEFERRED to P4 (when the drawer exists): swapping the two meanings, so the
//   bell becomes notifications and deadlines move to a clock. Considered and
//   deliberately not done here — it would change a live surface for no gain
//   while the notification click is still inert.
//
// NO CSS MODULE OF ITS OWN. It receives the header's `rightStyles` object and
// uses the existing headerIconBtn / headerBellBadge classes, so the deadline
// button renders pixel-identical to before the move.

import React from 'react';
import { useNotificationsStore } from '../../stores/notificationsStore';

// Lucide "megaphone", inlined. Deliberately NOT a second bell — two identical
// glyphs side by side would be unreadable. No icon dependency is added.
//
// `.headerIconBtn svg` in HeaderRight.module.css pins every icon to 15px /
// stroke-width 1.7, and a stylesheet rule beats an SVG presentation attribute —
// so the 17px / 1.9 this glyph wants has to arrive as an inline style, which
// does win. The megaphone reads lighter than the bell at identical weight;
// this is what keeps the pair looking like siblings.
const MEGAPHONE_STYLE = { width: 17, height: 17, strokeWidth: 1.9 };

export function Megaphone() {
  return (
    <svg viewBox="0 0 24 24" style={MEGAPHONE_STYLE} aria-hidden="true" focusable="false">
      <path d="m3 11 18-5v12L3 14v-3z" />
      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
    </svg>
  );
}

// 0 renders NO element at all — not a zero. Above `cap` renders "<cap>+":
// the megaphone caps at 9, the deadline bell at 99 (AUD-03 B5 — "1010" ran
// out of the bubble). The exact count stays in each button's title.
function badgeText(n, cap = 9) {
  if (!n || n <= 0) return null;
  return n > cap ? `${cap}+` : String(n);
}

export function HeaderBell({
  styles,
  deadlineCount = 0,
  onDeadlineClick,
  notificationCount = 0,
  onNotificationClick,
}) {
  const notifBadge = badgeText(notificationCount);
  const deadlineBadge = badgeText(deadlineCount, 99);
  // P4 — the megaphone opens the notification drawer. AppHeader.js passes no
  // onNotificationClick and is grandfathered at 417 lines, so the default lives
  // here rather than being threaded through a file that may not grow.
  const setShowDrawer = useNotificationsStore((s) => s.setShowDrawer);
  const openDrawer = onNotificationClick || (() => setShowDrawer(true));

  return (
    <>
      <button
        type="button"
        className={styles.headerIconBtn}
        onClick={onDeadlineClick}
        aria-label="Deadline alerts"
        data-test="header-deadline-bell"
        title={deadlineCount > 0 ? `${deadlineCount} deadline${deadlineCount === 1 ? '' : 's'} need attention` : 'No deadline alerts'}
      >
        <svg viewBox="0 0 24 24">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {deadlineBadge && (
          <span className={styles.headerBellBadge}>{deadlineBadge}</span>
        )}
      </button>

      <button
        type="button"
        className={styles.headerIconBtn}
        onClick={openDrawer}
        aria-label="Notifications"
        data-test="header-notifications"
        title={notificationCount > 0
          ? `${notificationCount} unread notification${notificationCount === 1 ? '' : 's'}`
          : 'No unread notifications'}
      >
        <Megaphone />
        {notifBadge && (
          <span className={styles.headerBellBadge} data-test="header-notifications-badge">
            {notifBadge}
          </span>
        )}
      </button>
    </>
  );
}

export default HeaderBell;
